import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { useCurrencies } from '../api/queries'

export function RatesAdminPage() {
  const { data: currencies } = useCurrencies()
  const queryClient = useQueryClient()

  const ratesQuery = useQuery({
    queryKey: ['fx-rates', 'current'],
    queryFn: async () => {
      const { data, error } = await api.GET('/fx-rates', { params: { query: {} } })
      if (error) throw error
      return data
    },
  })

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['fx-rates'] })
  }

  return (
    <div className="rates-admin">
      <h1>FX Rates</h1>
      <p className="hint">
        Rates are frozen onto each transaction at the moment it's recorded - adding a new rate only
        affects transactions entered from here on. To correct a mistaken rate retroactively, use
        Restate below, which re-freezes every transaction it now covers.
      </p>

      <table className="rates">
        <thead>
          <tr>
            <th>Currency</th>
            <th className="num">Rate to USD</th>
            <th>Effective from</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {(ratesQuery.data ?? []).map((r) => (
            <tr key={r.id}>
              <td>{r.currency}</td>
              <td className="num money">{r.rateToUsd}</td>
              <td>{r.effectiveFrom}</td>
              <td>{r.note ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="rate-forms">
        <AddRateForm currencies={currencies ?? []} onDone={invalidate} />
        <RestateForm currencies={currencies ?? []} onDone={invalidate} />
      </div>
    </div>
  )
}

function AddRateForm({
  currencies,
  onDone,
}: {
  currencies: { code: string }[]
  onDone: () => void
}) {
  const [currency, setCurrency] = useState('EUR')
  const [rate, setRate] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10))
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      const { error: apiError } = await api.POST('/fx-rates', {
        body: { currency, rate, effectiveFrom, note: note || null },
      })
      if (apiError) throw new Error(apiError.detail ?? apiError.title)
    },
    onSuccess: () => {
      setRate('')
      setNote('')
      setError(null)
      onDone()
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <form
      className="rate-form"
      onSubmit={(e) => {
        e.preventDefault()
        mutation.mutate()
      }}
    >
      <h2>Add a new rate</h2>
      <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
        {currencies.map((c) => (
          <option key={c.code} value={c.code}>
            {c.code}
          </option>
        ))}
      </select>
      <input
        type="text"
        inputMode="decimal"
        placeholder="USD per unit, e.g. 1.17"
        value={rate}
        onChange={(e) => setRate(e.target.value)}
        required
      />
      <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required />
      <input type="text" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <button type="submit" disabled={mutation.isPending}>
        Add rate
      </button>
      {error && <p className="error">{error}</p>}
    </form>
  )
}

function RestateForm({
  currencies,
  onDone,
}: {
  currencies: { code: string }[]
  onDone: () => void
}) {
  const [currency, setCurrency] = useState('EUR')
  const [rate, setRate] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      const { error: apiError } = await api.POST('/fx-rates/{code}/restate', {
        params: { path: { code: currency } },
        body: { effectiveFrom, rate, note: note || null },
      })
      if (apiError) throw new Error(apiError.detail ?? apiError.title)
    },
    onSuccess: () => {
      setRate('')
      setNote('')
      setError(null)
      onDone()
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <form
      className="rate-form"
      onSubmit={(e) => {
        e.preventDefault()
        mutation.mutate()
      }}
    >
      <h2>Restate a rate (corrects history)</h2>
      <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
        {currencies.map((c) => (
          <option key={c.code} value={c.code}>
            {c.code}
          </option>
        ))}
      </select>
      <input
        type="date"
        placeholder="Was effective from"
        value={effectiveFrom}
        onChange={(e) => setEffectiveFrom(e.target.value)}
        required
      />
      <input
        type="text"
        inputMode="decimal"
        placeholder="Corrected rate"
        value={rate}
        onChange={(e) => setRate(e.target.value)}
        required
      />
      <input
        type="text"
        placeholder="Why (recommended)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <button type="submit" disabled={mutation.isPending}>
        Restate
      </button>
      {error && <p className="error">{error}</p>}
    </form>
  )
}
